import { useEffect } from "react";
import { Case } from "@/types";
import { OutputDocument } from "@/types";
import { useAuthInfo } from "@propelauth/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getCase, getOutputDocument } from "@/utils/apiCalls";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { LoadingView } from "@/components/Loader";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { BreadcrumbList } from "@/components/ui/breadcrumb";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { DocumentDisplay } from "@/documents/DocumentDisplay";

const BreadcrumbNav = (props: {
  caseId: string;
  caseName: string;
  documentTypeName: string;
}) => {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/case/all">Cases</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink href={`/case/${props.caseId}`}>
            {props.caseName}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{props.documentTypeName}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export const OutputDocumentPage = () => {
  const { caseId, documentId } = useParams();
  const authInfo = useAuthInfo();
  const [document, setDocument] = useState<OutputDocument | null>(null);
  const [case_, setCase] = useState<Case | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCase = async () => {
    const response = await getCase(caseId ?? "", authInfo.accessToken ?? null);
    if (response === null) {
      toast.error("Failed to fetch case");
    } else {
      setCase(response);
    }
  };

  const fetchDocument = async () => {
    const response = await getOutputDocument(
      documentId ?? "",
      authInfo.accessToken ?? null
    );
    if (response === null) {
      toast.error("Failed to fetch document");
    } else {
      setDocument(response.document);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchCase(), fetchDocument()]);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [caseId, documentId]);

  return (
    <Layout title="Output Document">
      {isLoading ? (
        <LoadingView text="Loading output document..." />
      ) : (
        <>
          <BreadcrumbNav
            caseId={caseId ?? ""}
            caseName={`${case_?.first_name} ${case_?.last_name}`}
            documentTypeName={document?.document_type_name ?? ""}
          />
          {document && (
            <DocumentDisplay
              caseId={caseId ?? ""}
              document={document}
              setDocument={setDocument}
            />
          )}
        </>
      )}
    </Layout>
  );
};
